/** Gateway step (no stepper shown) — number + date dialog from Figma 5831-13900. */
export type GatewayStepKey = 'basics'

/** Wizard steps (stepper shown). */
export type WizardStepKey = 'subject' | 'landlord' | 'tenant' | 'terms' | 'payments'

export const WIZARD_STEP_ORDER: WizardStepKey[] = ['subject', 'landlord', 'tenant', 'terms', 'payments']

/** Rental object purpose — matches expected backend enum. */
export type ObjectPurpose = 'residence' | 'commercial' | 'mixed'

export const OBJECT_PURPOSES: ObjectPurpose[] = ['residence', 'commercial', 'mixed']

export interface PropertyOption {
	id:      string
	label:   string
	address: string
}

// ── Form-level values (UI-friendly types, strings from inputs) ─────────────

export interface BasicsValues {
	/** Contract number without the `№` prefix. */
	number:       string
	/** ISO date string `YYYY-MM-DD`. */
	dateIso:      string
	documentType: string
}

export interface SubjectServiceRow {
	id:        string
	/** Row selection (checkbox) — carried through to backend for bulk actions. */
	selected:  boolean
	name:      string
	qty:       number
	unit:      string
	/** Price per unit. Row sum is `qty * unitPrice` (computed, not stored). */
	unitPrice: number
	/**
	 * VAT rate for this row, in percent (e.g. `20` for 20%).
	 * Row prices are treated as VAT-inclusive (standard for RU rental contracts):
	 *   vatAmount = qty * unitPrice * rate / (100 + rate)
	 */
	vatRate:   number
}

export interface SubjectValues {
	/** Selected property id — source of truth for object reference. */
	propertyId: string
	/** Display label shown in the select/generated document (derived from property). */
	objectType: string
	/** Populated from picked property's address (user-editable afterwards). */
	address:         string
	cadastralNumber: string
	/** Raw input value; parsed to number on submit. */
	area:            string
	areaUnit:        string
	purpose:         ObjectPurpose
	services:        SubjectServiceRow[]
}

export interface LandlordValues {
	/** FK to BankAccount — picked from user's saved accounts. */
	bankAccountId: string
	/** FK to signatory (contact from user's org). */
	signatoryId:   string
	fullName:      string
	inn:           string
	organization:  string
	ogrn:          string
	legalAddress:  string
}

export type TenantType = 'individual' | 'entrepreneur' | 'company'

export const TENANT_TYPES: TenantType[] = ['individual', 'entrepreneur', 'company']

export interface TenantValues {
	/** FK to Contact when picked from CRM, empty string when free-text typed. */
	contragentId:        string
	/** Display name — auto-filled from CRM Contact, or freeform when typed. */
	contragentName:      string
	tenantType:          TenantType
	registrationAddress: string
	phone:               string
	passportSeries:      string
	passportNumber:      string
	passportIssuedBy:    string
	/**
	 * Registry identifiers. A commercial tenant is identified by these rather
	 * than by a passport — two different companies can share a name and an
	 * address, and the INN is what tells them apart in a contract.
	 * КПП exists only for a company; a sole trader and an individual have none.
	 */
	inn:                 string
	ogrn:                string
	kpp:                 string
	/** True when document is signed by an attorney (доверенность); reveals the
	 *  signatory + basis document fields below. */
	byProxy:             boolean
	/** Holds freeform signatory name (representative) until backend ships
	 *  `AccountSignatory[]` lists. */
	signatoryId:         string
	baseDocument:        string
	baseDocumentNumber:  string
	validFromIso:        string
	validToIso:          string
	/** Bank details. */
	bank:                string
	bankAccount:         string
	corrAccount:         string
	bic:                 string
}

// ── Terms (step 4 — Figma 5988-105076) ─────────────────────────────────────

export type RentTermKind = 'determined' | 'indeterminate'
export const RENT_TERM_KINDS: RentTermKind[] = ['determined', 'indeterminate']

export type TransferMode = 'act' | 'noAct'
export const TRANSFER_MODES: TransferMode[] = ['act', 'noAct']

export type TransferMoment = 'onSigning' | 'onActualTransfer'

export type ReturnCondition = 'usable' | 'normalWear' | 'withRepair' | 'clean'
export const RETURN_CONDITIONS: ReturnCondition[] = ['usable', 'normalWear', 'withRepair', 'clean']

export type ImprovementsFate = 'noCompensation' | 'compensated' | 'bySeparateAgreement' | 'custom'
export const IMPROVEMENTS_FATES: ImprovementsFate[] = [
	'noCompensation', 'compensated', 'bySeparateAgreement', 'custom',
]

export type AttachmentState = 'loading' | 'uploaded' | 'error'

export interface TermsAttachment {
	id:        string
	name:      string
	/** Human-readable size label — e.g. `234.6 КБ`. Raw bytes not required for the wizard. */
	sizeLabel: string
	state:     AttachmentState
}

export interface TermsValues {
	rentTermKind:          RentTermKind
	/** Договор действует с/по — ISO strings `YYYY-MM-DD`. */
	contractFromIso:       string
	contractToIso:         string
	/** Дата вступления договора в силу. */
	effectiveFromIso:      string
	/** Если true — `effectiveFromIso` синхронизируется с `contractFromIso`. */
	effectiveMatchesStart: boolean

	/** Сроки уведомления о расторжении — обе стороны. Unit is a reference key (`months`/`days`). */
	landlordNoticeCount:   number
	landlordNoticeUnit:    string
	tenantNoticeCount:     number
	tenantNoticeUnit:      string

	/** Передача помещения. */
	transferMode:                TransferMode
	transferActCount:            number
	transferActUnit:             string
	/** Free-text reference id of the timing option (e.g. `afterContractEnd`). */
	transferActTiming:           string
	transferMoment:              TransferMoment
	transferExecutorSignatoryId: string
	transferCustomerSignatoryId: string

	/** Возврат помещения. */
	returnMode:       TransferMode
	returnCount:      number
	returnUnit:       string
	returnTiming:     string
	returnConditions: ReturnCondition[]

	/** Неотделимые улучшения. */
	improvementsFate:    ImprovementsFate
	improvementsConsent: string

	/** Прочие условия — free text, pasted into clause 11.6. */
	otherConditions: string

	/** Uploaded attachments — client-side only until backend #63 ships. */
	attachments: TermsAttachment[]
}

// ── Payments (step 5 — Figma 5988-106338) ──────────────────────────────────

export interface PaymentsUtilityRow {
	id:       string
	name:     string
	/** Reference key for calculation mode (`fixed` | `byMeters` | ...). */
	calcType: string
	/** `null` means "Расчет при аренде" — amount will be set later by the tenant side. */
	amount:   number | null
}

export type PaymentPeriodStart = 'monthStart' | 'yearStart' | 'contractStart'
export const PAYMENT_PERIOD_STARTS: PaymentPeriodStart[] = ['monthStart', 'yearStart', 'contractStart']

export interface PaymentsValues {
	/** Размер арендной платы. */
	rentAmount:       number
	/** Currency reference key (`RUB` etc). */
	rentCurrency:     string
	rentVatRate:      number
	/** Готовая юридическая формулировка с сервера; собирается по основанию ставки. */
	rentVatClause?:   string
	rentVatInclusive: boolean

	/** Коммунальные и эксплуатационные расходы. */
	utilities: PaymentsUtilityRow[]

	/** Обеспечительный платеж (секция с тумблером). */
	depositEnabled:         boolean
	depositAmount:          number
	depositCurrency:        string
	depositTermCount:       number
	depositTermUnit:        string
	depositTermTiming:      string
	depositReturn:          string
	depositNoReturnOnEarly: boolean

	/** Пени за просрочку (секция с тумблером). */
	penaltyEnabled:    boolean
	penaltyPercent:    number
	penaltyGraceCount: number
	penaltyGraceUnit:  string

	/** Срок внесения платежа за первый период — число дней. */
	firstPaymentDays:  number

	/** Порядок перечисления арендной платы. */
	paymentForm:       string
	/** FK to BankAccount — отображается только для безналичной формы. */
	landlordAccountId: string

	/** Условия оплаты. */
	periodStart:     PaymentPeriodStart
	paymentDay:      string
	paymentPriority: string
}

export interface WizardState {
	basics:   BasicsValues
	subject:  SubjectValues
	landlord: LandlordValues
	tenant:   TenantValues
	terms:    TermsValues
	payments: PaymentsValues
}

// ── Backend payload shape (mirrors Prisma `Document.create` input) ─────────
// Keeps the wizard front of backend API (#63). Once backend exists the
// mutation body will accept this shape 1:1.

export interface CreateDocumentService {
	name:      string
	unit:      string
	qty:       number
	unitPrice: number
	/** VAT rate in percent — backend applies the same inclusive-price formula as the UI. */
	vatRate:   number
}

/**
 * Human-readable strings computed from enum values in WizardState.
 * Used for template substitution via fieldKey prefix "display.*.*".
 * All fields are plain Russian strings ready for direct insertion into PDF.
 */
export interface DocumentDisplay {
	basics: {
		/** Contract date formatted as DD.MM.YYYY (e.g. "22.06.2026") */
		date: string
	}
	subject: {
		/** «проживание» / «коммерческая деятельность» / «смешанное использование» */
		purpose: string
	}
	terms: {
		/** "определённый" / "неопределённый" */
		rentTermKind:       string
		/** Contract start date formatted as DD.MM.YYYY */
		contractFrom:       string
		/** Contract end date formatted as DD.MM.YYYY */
		contractTo:         string
		/** Effective (entry) date formatted as DD.MM.YYYY */
		effectiveFrom:      string
		/** "3 месяца" / "14 дней" */
		landlordNotice:     string
		tenantNotice:       string
		/** "по акту приёма-передачи" / "без составления акта" */
		transferMode:       string
		/** "5 дней" */
		transferActPeriod:  string
		/** "после окончания срока договора" / "в согласованную дату" */
		transferActTiming:  string
		/** "с момента подписания договора" / "с момента фактической передачи" */
		transferMoment:     string
		returnMode:         string
		returnPeriod:       string
		returnTiming:       string
		/** "в чистоте, с учётом нормального износа" */
		returnConditions:   string
		/** "без компенсации арендодателем" */
		improvementsFate:   string
	}
	payments: {
		/** "144 040 ₽" — formatted rent amount with currency symbol */
		rentAmount:         string
		/** "0,08" — penalty rate formatted with locale decimal separator */
		penaltyPercent:     string
		/** "включая НДС 12%" / "НДС не облагается" */
		vatClause:          string
		/** "безналичным банковским переводом" / "наличными" */
		paymentForm:        string
		/** "5-го числа" */
		paymentDay:         string
		/** "с первого числа текущего месяца" */
		periodStart:        string
		/** "3 рабочих дня" */
		firstPaymentPeriod: string
		/** "50 000 ₽" / "не предусмотрен" */
		depositClause:      string
		/** "3 месяца" */
		depositTermPeriod:  string
		/** "после окончания срока договора" */
		depositTiming:      string
		/** "засчитывается в счёт оплаты последнего периода" */
		depositReturn:      string
		/** "3 дня" */
		penaltyGrace:       string
	}
	tenant: {
		/** "действующего на основании доверенности №…" or "" if no proxy */
		proxyClause: string
	}
}

export interface CreateDocumentPayload {
	/** FK — Deal that owns this document. */
	dealId:       string
	/** FK — DocumentTemplate used as a base. */
	templateId:   string
	title:        string
	documentType: string
	/** Contract basics — maps to fieldKey prefix "basics.*" */
	basics: {
		number:  string
		dateIso: string
	}
	/** Property/object info — maps to fieldKey prefix "subject.*" */
	subject: {
		propertyId:      string
		objectType:      string
		address:         string
		cadastralNumber: string
		area:            number
		purpose:         ObjectPurpose
		services:        CreateDocumentService[]
	}
	landlord: LandlordValues
	tenant:   TenantValues
	terms:    TermsValues
	payments: PaymentsValues
	/** Pre-formatted Russian strings for template substitution. */
	display:  DocumentDisplay
}
